/*
 * Carbisdale - EditorCleanup
 *
 * Cleanup-on-Leave Regeln für den Editor.
 * Wird aufgerufen wenn der Cursor einen Paragraph verlässt.
 *
 * Im Rich-Text-Modus gibt es keine Markdown-Marker im Text —
 * Formate (Bold, Italic etc.) und Block-Typen (Heading, Liste etc.)
 * werden über Spans bzw. Paragraph-Metadaten gespeichert.
 * Cleanup beschränkt sich daher auf Whitespace und Interpunktion.
 *
 * Span-aware: jede Edit-Operation passt parallel die Format-Spans und Links
 * an, sonst wandern Spans aus ihrer Text-Position. Code-Spans (`type:'code'`)
 * sind zusaetzlich vor allen Cleanup-Regeln geschuetzt — Backticks duerfen
 * z.B. einen `.` enthalten (`console.log`), ohne dass die Punctuation-Regel
 * darin einen Space einfuegt. Lesson 87.
 */

import {
    adjustSpansForInsert,
    adjustSpansForDeleteRange,
    adjustLinksForInsert,
    adjustLinksForDeleteRange
} from '../RichTextOps.js'

/**
 * EditorCleanup - Statische Methoden für Paragraph-Bereinigung
 */
export class EditorCleanup {
    /**
     * Führt alle Cleanup-Regeln auf einem Paragraph aus.
     * @param {EditorModel} model
     * @param {number} paragraphId
     */
    static cleanupParagraph(model, paragraphId) {
        const para = model._mutate(model.getParagraph(paragraphId))
        if (!para) return

        // Separatoren brauchen kein Cleanup (Typ darf nicht überschrieben werden)
        if (para.type === 'separator') return

        // Originalen Text speichern für Vergleich
        const originalText = para.text
        const originalNote = para.note

        // text-Feld span-aware cleanen (inklusive Code-Span-Schutz).
        // Code-Block-Paras sind komplett raw: keine Trim-/Multi-Space-/
        // Punctuation-Korrekturen — Leerstellen sind semantisch (Indentation).
        if (para.text.length > 0 && !para.codeBlock) {
            this._cleanupField(para, 'text')
        }

        // Note span-aware cleanen (Note hat kein codeBlock-Konzept)
        if (para.note.length > 0) {
            this._cleanupField(para, 'note')
        }

        // Dirty markieren wenn geändert
        if (para.text !== originalText || para.note !== originalNote) {
            model._markParagraphDirty(paragraphId)
        }
    }

    /**
     * Cleanup eines einzelnen Feldes (text oder note) mit Span-/Link-Adjustment.
     * Sammelt Edits, filtert Edits die in Code-Spans liegen, und wendet sie
     * von hinten nach vorne an, damit frühere Offsets stabil bleiben.
     */
    static _cleanupField(para, field) {
        const text = para[field]
        const formats = para.formats?.[field] || []
        const codeRanges = formats.filter(s => s.type === 'code')
        const inCode = (pos) => codeRanges.some(s => pos >= s.start && pos < s.end)

        // Inner-Region (zwischen führendem und nachfolgendem Whitespace)
        // bestimmen — Multi-Space- und Punctuation-Regeln laufen NUR darin,
        // sonst überlappen sie mit Trim-Edits und doppelte deletes löschen
        // anliegende Wortzeichen.
        let leadEnd = 0
        while (leadEnd < text.length && this._isWS(text[leadEnd])) leadEnd++
        let trailStart = text.length
        while (trailStart > leadEnd && this._isWS(text[trailStart - 1])) trailStart--

        const edits = []

        // 1. Trim leading whitespace
        if (leadEnd > 0) {
            edits.push({ type: 'delete-range', start: 0, end: leadEnd })
        }

        // 2. Trim trailing whitespace
        if (trailStart < text.length) {
            edits.push({ type: 'delete-range', start: trailStart, end: text.length })
        }

        const innerText = text.substring(leadEnd, trailStart)
        let m

        // 3. Multi-Space → Single-Space (kollabiere n>1 spaces) im Inner-Bereich
        const multiSpaceRegex = /  +/g
        while ((m = multiSpaceRegex.exec(innerText)) !== null) {
            const collapseStart = leadEnd + m.index + 1
            const collapseEnd = leadEnd + m.index + m[0].length
            if (!inCode(leadEnd + m.index)) {
                edits.push({ type: 'delete-range', start: collapseStart, end: collapseEnd })
            }
        }

        // 4. Whitespace vor Punctuation entfernen ([.!?,;:])
        const wsBeforePunctRegex = /\s+([.!?,;:])/g
        while ((m = wsBeforePunctRegex.exec(innerText)) !== null) {
            const wsLen = m[0].length - 1
            const punctPosInner = m.index + wsLen
            const wsStart = leadEnd + m.index
            const punctPos = leadEnd + punctPosInner
            // Wenn die Punctuation oder die Whitespace-Stelle im Code liegt, skippen
            if (!inCode(wsStart) && !inCode(punctPos)) {
                edits.push({ type: 'delete-range', start: wsStart, end: punctPos })
            }
        }

        // 5. Space nach Sentence-Punctuation vor Buchstabe einfügen ([.!?][A-Za-z])
        const spaceAfterPunctRegex = /([.!?])([A-Za-z])/g
        while ((m = spaceAfterPunctRegex.exec(innerText)) !== null) {
            const punctPos = leadEnd + m.index
            const letterPos = leadEnd + m.index + 1
            // Kritischer Schutz: `console.log` → der `.` zwischen `e` und `l`
            // ist Teil eines Code-Spans und darf nicht "korrigiert" werden.
            if (!inCode(punctPos) && !inCode(letterPos)) {
                edits.push({ type: 'insert', offset: letterPos, ch: ' ' })
            }
        }

        if (edits.length === 0) return

        this._applyEdits(para, field, edits)
    }

    /**
     * Wendet Edits an Text + Format-Spans + Links an. Edits werden in
     * Reverse-Order ausgeführt (hohe Position zuerst), damit frühere
     * Offsets stabil bleiben — kein laufender Offset-Shift nötig.
     */
    static _applyEdits(para, field, edits) {
        // Sortieren: höhere Position zuerst. Bei gleicher Position kommt insert
        // vor delete-range (sonst würde delete-range den insert-Anker verschieben).
        edits.sort((a, b) => {
            const aPos = a.type === 'delete-range' ? a.start : a.offset
            const bPos = b.type === 'delete-range' ? b.start : b.offset
            return bPos - aPos
        })

        let text = para[field]
        let formats = para.formats?.[field] || []
        let links = para.links?.[field] || []

        for (const edit of edits) {
            if (edit.type === 'insert') {
                text = text.substring(0, edit.offset) + edit.ch + text.substring(edit.offset)
                formats = adjustSpansForInsert(formats, edit.offset, edit.ch.length)
                links = adjustLinksForInsert(links, edit.offset, edit.ch.length)
            } else if (edit.type === 'delete-range') {
                text = text.substring(0, edit.start) + text.substring(edit.end)
                formats = adjustSpansForDeleteRange(formats, edit.start, edit.end)
                links = adjustLinksForDeleteRange(links, edit.start, edit.end)
            }
        }

        para[field] = text
        if (para.formats) para.formats[field] = formats
        if (para.links) para.links[field] = links
    }

    static _isWS(ch) {
        return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r'
    }
}
